import {useMemo, useState} from 'react';
import {buttonVariants} from '@ezcater/tapas';
import useTranslation from 'next-translate/useTranslation';

import {useConsumerCart} from '@/components/features/menu/hooks';
import {
  JumpBackInHomepageExperimentVariation,
  useJumpBackInHomepage,
} from '@/components/global/providers/JumpBackInHomepageProvider/JumpBackInHomepageProvider';
import {useMobileCartContext} from '@/components/global/providers/MobileCartProvider';
import {useSelectedCorpAccountUuidContext} from '@/components/global/providers/SelectedCorpAccountUuidProvider';
import {useStoreNextRouter} from '@/components/global/providers/StoreNextRouter';
import {LoadingSpinner} from '@/components/ui/Loading';
import Modal from '@/components/ui/Modal';
import {OpenOrdersQuery, useOpenOrdersQuery} from '@/graphql/types';
import useExcludeModificationCarts from '@/hooks/useExcludeModificationCarts';
import useOpenOrdersFulfillmentDetailFilters from '@/hooks/useOpenOrdersFulfillmentDetailFilters';
import useTracking from '@/hooks/useTracking';
import {ORDERS_PATH} from '@/paths';
import {compilePath} from '@/utils';
import appsFlyerDismissal from '@/utils/appsFlyerDismissal';
import {filterByCorpAccount} from '@/utils/filterByCorpAccount';
import RequiredPath from '@/utils/RequiredPath';
import {NAVLINK_TRACKING} from '../appBarTracking';
import useFofNavbarPhase2a from '../hooks/useFofNavbarPhase2a';
import useNavbarVersion from '../hooks/useNavbarVersion';
import useNavLinkTracking from '../hooks/useNavlinkTracking';
import MenuPopover from '../Menus/MenuPopover';
import useDraftCartTracking from './tracking/useDraftCartTracking';
import CartButton from './CartButton';
import CartOpenTracker, {type DraftCartDropdownState} from './CartOpenTracker';
import DraftCartDropdown from './DraftCartDropdown';
import DraftOrder from './DraftOrder';
import EmptyState from './EmptyState';

export type Order = {
  id: string;
  items: {
    id: string;
    name: string;
    quantity: number;
  }[];
  totalDue: {
    currency: string;
    subunits: number;
  };
  caterer: {
    id: string;
    urlId: string;
    name: string;
    address?: {
      id: string;
      city: string;
      state: string;
      fullAddress?: string;
      nameOrLocation?: string;
      street?: string;
      timeZoneIdentifier?: string;
      uuid?: string;
    };
    brand?: {
      id: string;
      heroImage?: {
        uuid: string;
        mobileHeroUrl?: string | null;
      };
    };
  };
  eventName?: string | null;
  eventAt?: string | null;
  address?: {
    id: string;
    name?: string | null;
    fullAddress?: string | null;
  };
  updatedAt: string;
  accountName?: string;
  isModification: boolean;
  groupOrder?: object | null;
};

type FulfillmentDetailConnection = RequiredPath<
  OpenOrdersQuery,
  ['identity', 'fulfillmentDetails']
>;

type FulfillmentDetailConnectionConsumerCart = RequiredPath<
  FulfillmentDetailConnection,
  ['edges', number, 'node', 'consumerCarts', number]
>;

type FormatConsumerCartParams = {
  consumerCart: FulfillmentDetailConnectionConsumerCart;
  eventName?: string | null;
  eventAt?: string | null;
  address?: {
    id: string;
    name?: string | null;
    fullAddress?: string;
  };
  updatedAt: string;
  accountName?: string;
};

const formatConsumerCart = ({
  consumerCart,
  eventName,
  eventAt,
  address,
  updatedAt,
  accountName,
}: FormatConsumerCartParams): Order => {
  return {
    eventName,
    eventAt,
    address,
    id: consumerCart.id,
    isModification: consumerCart.isModification,
    items: consumerCart.items.map(item => ({
      id: item.id,
      name: item.name ?? '',
      quantity: item.quantity,
    })),
    totalDue: {
      currency: consumerCart.totalDue.currency,
      subunits: consumerCart.totalDue.subunits,
    },
    caterer: {
      ...consumerCart.caterer,
      address: consumerCart.caterer.address ?? undefined,
      brand: consumerCart.caterer.brand
        ? {
            id: consumerCart.caterer.brand.id,
            heroImage: consumerCart.caterer.brand.heroImage ?? undefined,
          }
        : undefined,
    },
    updatedAt,
    accountName,
  };
};

const formatOrders = (
  fulfillmentDetailConnection?: FulfillmentDetailConnection | null,
  accountUuid?: string | null,
) => {
  if (!fulfillmentDetailConnection?.edges) return [];

  const filteredEdges = filterByCorpAccount(fulfillmentDetailConnection.edges, accountUuid) ?? [];

  return filteredEdges
    .flatMap(fulfillmentDetailEdge => {
      const fulfillmentDetail = fulfillmentDetailEdge.node;
      if (!fulfillmentDetail?.consumerCarts) return null;

      return fulfillmentDetail.consumerCarts.map(cart =>
        formatConsumerCart({
          consumerCart: cart,
          eventName: fulfillmentDetail.eventName,
          eventAt: fulfillmentDetail.eventAt,
          address: fulfillmentDetail.address,
          updatedAt: cart.updatedAt,
          accountName: fulfillmentDetail?.corpAccount?.name,
        }),
      );
    })
    ?.filter(cart => cart != null);
};

const filterPastOrder = (order: Order) => {
  if (!order.eventAt) return true;
  const eventDate = new Date(order.eventAt);
  const now = new Date();
  return eventDate >= now;
};

const groupAndSortOrders = (orders: Order[]): Order[] => {
  return [...orders].sort((a, b) => {
    // Sorting by last order interaction
    const updatedAtA = new Date(a.updatedAt).getTime();
    const updatedAtB = new Date(b.updatedAt).getTime();

    if (!updatedAtA || !updatedAtB) {
      // Neither has a updatedAt date to sort by, sort by presence of event name
      if (!a.eventName && b.eventName) return -1;
      if (a.eventName && !b.eventName) return 1;
      return 0;
    } else {
      return updatedAtB - updatedAtA;
    }
  });
};

const Cart: React.FC = () => {
  const {t} = useTranslation('app-bar');
  const {track} = useTracking();
  const {trackSeeAllClick} = useDraftCartTracking();
  const {trackRumAction} = useNavLinkTracking();
  // The draft cart dropdown is changed in FOF navbar phase 2.
  // On mobile, the draft cart button behaves differently.
  // We need to ensure this case is excluded from tracking.
  const draftCartVersion = useNavbarVersion('draftCart');
  const menuPageCartVersion = useNavbarVersion();
  const {isFofNavbarPhase2aTreatment, trackExposure} = useFofNavbarPhase2a();
  const excludeModificationCarts = useExcludeModificationCarts();
  const fulfillmentDetailFilters = useOpenOrdersFulfillmentDetailFilters();
  const {data, loading, error} = useOpenOrdersQuery({
    variables: {...fulfillmentDetailFilters, excludeModificationCarts},
  });
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [cartIsOpen, setCartIsOpen] = useState(false);
  const [keepCartOpen, setKeepCartOpen] = useState(false);
  const {accountUuid} = useSelectedCorpAccountUuidContext();
  const {isOnMenuPage, isOnLoggedOutLandingPage} = useStoreNextRouter();
  const {isModalOpen: isMobileCartModalOpen, setIsModalOpen: setIsMobileCartModalOpen} =
    useMobileCartContext();

  const trackCartButtonHover = (version: string) => {
    track('cart-button-hovered', {
      page: window.location.pathname,
      sub_category: 'navigation bar',
      misc_json: JSON.stringify({version}),
    });
  };

  const trackCartButtonClick = (version: string) => {
    trackRumAction({datadogAction: NAVLINK_TRACKING.cart.clicked, version});
  };

  const handleMobileCartModalOpen = () => {
    trackCartButtonClick(menuPageCartVersion);
    appsFlyerDismissal();
    setIsMobileCartModalOpen(!isMobileCartModalOpen);
  };

  const handleMobileDraftsButtonClick = () => {
    trackCartButtonClick(draftCartVersion);
    appsFlyerDismissal();
  };

  const orders = useMemo(
    () => formatOrders(data?.identity?.fulfillmentDetails, accountUuid).filter(filterPastOrder),
    [data, accountUuid],
  );
  const hasCarts = orders.length > 0;

  const groupedOrders = useMemo(() => groupAndSortOrders(orders), [orders]);
  const showLoadingSpinner = loading && !hasCarts;

  // Dropdown is states, for tracking.
  // Failed query renders the `EmptyState`,
  // so this differentiates "no drafts" and "query failed"
  let dropdownState: DraftCartDropdownState = 'list';
  if (showLoadingSpinner) {
    dropdownState = 'loading';
  } else if (!hasCarts) {
    dropdownState = error ? 'error' : 'empty';
  }

  const menuPageCart = useConsumerCart();
  const menuPageCartItemCount =
    menuPageCart?.items?.reduce((total, item) => total + (item?.quantity || 0), 0) || 0;

  // ezx566::ARC::JumpBackIn start
  const {variation: jumpBackInHomepageVariation, hasFulfillmentDetails} = useJumpBackInHomepage();

  const shouldShowJumpBackInHomepage =
    jumpBackInHomepageVariation === JumpBackInHomepageExperimentVariation.TREATMENT &&
    hasFulfillmentDetails;
  const draftCartCountClassName = shouldShowJumpBackInHomepage ? 'text-peppercorn-800' : undefined;

  const shouldBounceCart = shouldShowJumpBackInHomepage && isOnLoggedOutLandingPage && hasCarts;
  // ezx566::ARC::JumpBackIn end

  let content: React.ReactNode;

  if (showLoadingSpinner) {
    content = <LoadingSpinner />;
  } else if (!hasCarts) {
    content = <EmptyState />;
  } else {
    content = (
      <div className="flex flex-col gap-4">
        <h3 className="mb-2 hidden font-bold tablet:block">
          {t('cart.title', {numberDrafts: groupedOrders.length})}
        </h3>

        {groupedOrders.map((order, index) => (
          <div key={order.id}>
            <DraftOrder order={order} position={index + 1} setKeepCartOpen={setKeepCartOpen} />
            <div className="mb-3 mt-7 h-px bg-gray-200"></div>
          </div>
        ))}

        <a
          className={buttonVariants({
            className: 'hidden w-full text-center tablet:block',
            size: 'medium',
            variant: 'outlined',
          })}
          href={compilePath(ORDERS_PATH, {}, {tab: 'DRAFTS'})}
          onClick={() => trackSeeAllClick()}
        >
          {t('cart.seeAllDrafts')}
        </a>
      </div>
    );
  }

  const handleCartOpenStateChange = (isOpen: boolean) => {
    if (isOpen) trackExposure();
    // If keepCartOpen is true, override and keep the cart open
    // Otherwise, use the cart's original isOpen status
    setCartIsOpen(keepCartOpen || isOpen);
  };

  const handleMobileModalOpenChange = (isOpen: boolean) => {
    if (isOpen) trackExposure();
    setIsMobileModalOpen(isOpen);
  };

  const dropdownContent = isFofNavbarPhase2aTreatment ? <DraftCartDropdown /> : content;

  return (
    <>
      <div className={isOnMenuPage ? 'hidden tablet:block' : 'hidden appbar-md:block'}>
        <MenuPopover
          arrowClasses="right-0.5"
          className="w-[362px] px-3"
          content={
            <div className="w-full px-1 text-black">
              <CartOpenTracker area="desktop" carts={groupedOrders} state={dropdownState} />
              {dropdownContent}
            </div>
          }
          contentClasses="max-h-[640px]"
          isOpen={cartIsOpen}
          onOpenChange={handleCartOpenStateChange}
          placement="bottom-end"
        >
          <CartButton
            className={shouldBounceCart ? 'motion-safe:animate-[draft-cart-pop_0.6s_1]' : undefined}
            count={isOnMenuPage ? undefined : groupedOrders.length}
            data-testid="desktop-cart-button"
            draftCountClassName={draftCartCountClassName}
            onClick={() => trackCartButtonClick(draftCartVersion)}
            onMouseEnter={() => trackCartButtonHover(draftCartVersion)}
            variant={isOnLoggedOutLandingPage ? 'contrast' : 'secondary'}
          />
        </MenuPopover>
      </div>
      <div className={isOnMenuPage ? 'tablet:hidden' : 'appbar-md:hidden'}>
        {isOnMenuPage ? (
          <CartButton
            count={menuPageCartItemCount}
            data-testid="mobile-cart-button"
            onClick={handleMobileCartModalOpen}
            onMouseEnter={() => trackCartButtonHover(menuPageCartVersion)}
          />
        ) : (
          <Modal
            footer={
              <div className="flex justify-center px-6 py-4 tablet:justify-end">
                <a
                  className={buttonVariants({
                    className: 'w-full tablet:w-auto',
                    size: 'medium',
                    variant: 'outlined',
                  })}
                  href={compilePath(ORDERS_PATH, {}, {tab: 'DRAFTS'})}
                  onClick={() => trackSeeAllClick()}
                >
                  {t('cart.seeAllDrafts')}
                </a>
              </div>
            }
            open={isMobileModalOpen}
            onOpenChange={handleMobileModalOpenChange}
            title={t('cart.title', {numberDrafts: groupedOrders.length})}
            trigger={
              <CartButton
                className={
                  shouldBounceCart ? 'motion-safe:animate-[draft-cart-pop_0.6s_1]' : undefined
                }
                count={groupedOrders.length}
                draftCountClassName={draftCartCountClassName}
                data-testid="mobile-cart-button"
                onClick={handleMobileDraftsButtonClick}
                onMouseEnter={() => trackCartButtonHover(draftCartVersion)}
                variant={isOnLoggedOutLandingPage ? 'contrast' : 'secondary'}
              />
            }
          >
            <>
              <CartOpenTracker area="mobile" carts={groupedOrders} state={dropdownState} />
              {dropdownContent}
            </>
          </Modal>
        )}
      </div>
    </>
  );
};

export default Cart;
